import React, { useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TextField, FormControl, FormHelperText, InputLabel, Select, MenuItem, Card, Typography, Grid } from '@mui/material';
import { useAPIUnauthenticatedService  } from '../helpers/APICallsUnauthenticated';
import { InHouseCardHeader, InHouseCardContent } from '../components/CustomMUI';
import { UserCreationAsPropertyProfessionalAttributes, CacheKeys } from '../helpers/DataStructures';
import { useAppCommands } from '../appCore/AppRuntimeReact';
import AsyncActionButton, { AsyncActionButtonAbortError } from '../components/AsyncActionButton';

interface CreateUserUnauthenticatedFormProps {
    onDone: () => void;
}

const CreateUserUnauthenticatedForm: React.FC<CreateUserUnauthenticatedFormProps> = ({ onDone }) => {
    const usersEmailRef = useRef('');
    const { requestSignup } = useAppCommands();

    const { handleSubmit, control, formState: { errors } } = useForm<UserCreationAsPropertyProfessionalAttributes>();
    const queryClient = useQueryClient();

    const apiServiceInstance = useAPIUnauthenticatedService();

//   const { isLoading, isError, data: data_loggedInUser, error } = useQuery(
//     [CacheKeys.LOGGED_IN_USER], 
//     async () => {
      
//         const user = await apiServiceInstance.getAuthenticatedUser();
//         return user;      
//     }
//   ); 

  const { mutateAsync } =
    useMutation([CacheKeys.LOGGED_IN_USER], apiServiceInstance.createUser,
    {
        onSuccess: () => {
            queryClient.invalidateQueries([CacheKeys.LOGGED_IN_USER]);
        }
    },
  );

  const submitUser = handleSubmit(
    async (data: UserCreationAsPropertyProfessionalAttributes) => {
      usersEmailRef.current = data.email;
      await mutateAsync(data);
    },
    async () => {
      throw new AsyncActionButtonAbortError();
    }
  );

  return (
        <Card variant="outlined"  style={{ margin: '0 auto' }}>
            <InHouseCardHeader title="Welcome to In House" />
            <InHouseCardContent>      
                <Typography variant="cardSubtitle" component="div" style={{ marginBottom: '1em' }}>
                    Please provide your details...
                </Typography>
                <form>
                    <Grid container direction="column" spacing={2}>
                        <Grid item xs={12}>
                            <Controller
                                
                                name="givenName"
                                control={control}
                                defaultValue=""
                                rules={{ required: 'First name is required', maxLength: { value: 255, message: 'First name should be less than 255 characters'} }}
                                render={({ field }) => <TextField {...field} label="First Name" error={Boolean(errors.givenName)} helperText={errors.givenName?.message} fullWidth />}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="familyName"
                                control={control}
                                defaultValue=""
                                rules={{ required: 'Last name is required', maxLength: { value: 255, message: 'Last name should be less than 255 characters'} }}
                                render={({ field }) => <TextField {...field} label="Last Name" error={Boolean(errors.familyName)} helperText={errors.familyName?.message} fullWidth/>}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="email" // Specify the field name
                                control={control}
                                defaultValue=""
                                rules={{
                                required: 'Email is required',
                                pattern: {
                                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                    message: 'Invalid email format',
                                },
                                maxLength: { value: 255, message: 'Email should be less than 255 characters' },
                                }}
                                render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Email"
                                    error={Boolean(errors.email)}
                                    helperText={errors.email?.message}
                                    fullWidth
                                />
                                )}
                            />
                            </Grid>

                        <Grid item xs={12}>
                            <Controller
                                name="profession"
                                control={control}
                                defaultValue=""
                                rules={{ required: 'Profession is required' }}
                                render={({ field }) => (
                                <FormControl fullWidth>
                                    <InputLabel id="profession-label">Profession</InputLabel>
                                    <Select
                                    {...field}
                                    labelId="profession-label"
                                    label="Profession"
                                    error={Boolean(errors.profession)}
                                    > 
                                    <MenuItem value="estate agent">Estate Agent</MenuItem>
                                    <MenuItem value="surveyor">Surveyor</MenuItem>
                                    {/* REQ-039: signup profession labels must use the correct public spelling. */}
                                    <MenuItem value="conveyancer">Conveyancer</MenuItem>
                                    <MenuItem value="mortgage broker">Mortgage Broker</MenuItem>
                                    <MenuItem value="epc assessor">EPC Assessor</MenuItem>

                                    <MenuItem value="removal and storage">Removal and Storage</MenuItem>
                                    <MenuItem value="damp and timber">Damp and Timber</MenuItem>
                                    <MenuItem value="asbestos surveyors">Asbestos Surveyors</MenuItem>
                                    <MenuItem value="drain engineers">Drain Engineers</MenuItem>
                                    <MenuItem value="house cleaners">House Cleaners</MenuItem>  
                                    
                                    <MenuItem value="electrician">Electrician</MenuItem>  
                                    <MenuItem value="plumber">Plumber</MenuItem>
                                    {/* REQ-038: property professional signup must expose every supported profession. */}
                                    <MenuItem value="property maintenance">Property Maintenance</MenuItem>


                                    </Select>
                                    {errors.profession && <FormHelperText error>{errors.profession.message}</FormHelperText>}
      
                                </FormControl>
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="companyName"
                                control={control}
                                defaultValue=""
                                
                                rules={{ maxLength: { value: 255, message: 'Company name should be less than 255 characters'} }}
                                render={({ field }) => <TextField {...field} label="Company Name" error={Boolean(errors.companyName)} helperText={errors.companyName?.message} fullWidth/>}
                            />
                        </Grid>

                        <Grid item xs={12}  sx={{textAlign:'center'}}>
                            <AsyncActionButton
                              errorToast="Error"
                              fullWidth
                              idleLabel="Submit"
                              onAction={() => submitUser()}
                              onAfterSuccessDisplay={async () => {
                                onDone();
                                await requestSignup({
                                  loginHint: usersEmailRef.current,
                                  returnTo: `${window.location.origin}/dashboard`,
                                  redirectUri: `${window.location.origin}/dashboard`,
                                });
                              }}
                              pendingLabel="Submitting..."
                              styleVariant="green"
                              successDisplayMs={300}
                              successLabel="Redirecting..."
                            />
                        </Grid>
                    </Grid>
                </form>
            </InHouseCardContent>
        </Card>
  );
};

export default CreateUserUnauthenticatedForm
